import axios from "axios";
import conditionalCustomFieldsApi from "./conditionalCustomFieldsApi";

/**
 * Strictly save custom field values for an entity with 0/1 conditional logic.
 * 0 = Do NOT trigger the custom fields API (plugin disabled)
 * 1 = Trigger the custom fields API (plugin enabled)
 * Throws on any failure.
 *
 * @param {Object} params
 * @param {string} params.baseURL - Base URL for API (e.g., process.env.REACT_APP_Baseurl)
 * @param {Object} params.headers - Headers object (e.g., from getItemToken('access_token'))
 * @param {string} params.entityType - Entity type key expected by backend (e.g., 'plan', 'faq')
 * @param {string|number} params.entityId - Entity identifier to attach custom fields to
 * @param {Object} params.values - Custom field values object from FieldRenderer onChange
 * @returns {Promise<Object>} The API response data if successful, null if plugin disabled
 * @throws {Error} If request fails or backend indicates failure
 */
export default async function saveCustomFieldsStrict({ baseURL, headers, entityType, entityId, values }) {
  // Check if custom fields plugin is enabled (1 = enabled, 0 = disabled)
  const isEnabled = await conditionalCustomFieldsApi.isCustomFieldsEnabled();
  
  if (!isEnabled) {
    console.log('[CustomFields] Save operation skipped - plugin is DISABLED (0)');
    return { status: true, message: 'Custom fields plugin is disabled, skipping save' };
  }
  
  console.log('[CustomFields] Save operation proceeding - plugin is ENABLED (1)');
  
  if (!baseURL) throw new Error("Missing baseURL for saving additional fields");
  if (!entityType) throw new Error("Missing entity type for saving additional fields");
  if (entityId === undefined || entityId === null || entityId === "") {
    throw new Error("Missing entity id for saving additional fields");
  }

  try {
    const res = await axios.post(
      `${baseURL}/admin/custom-field-values`,
      { entity_type: entityType, entity_id: entityId, values },
      { headers }
    );
    const data = res?.data;
    if (!data?.status) {
      throw new Error(data?.message || "Failed to save additional fields");
    }
    return data;
  } catch (e) {
    const msg = e?.response?.data?.message || e?.message || "Failed to save additional fields";
    throw new Error(msg);
  }
}

/**
 * Legacy function for backward compatibility
 * Uses the conditional save functionality
 */
export const saveCustomFieldsConditional = saveCustomFieldsStrict;

